import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, mediaUrl } from "@/lib/oraya-api";
import { IMG } from "@/data/site";
import { Seo, Reveal, Overline, GlassCard, SectionHeader } from "@/components/site/primitives";

function statusLabel(s) {
  return s === "active" ? "Active System" : s === "coming_soon" ? "Coming Soon" : "Future System";
}

export default function Systems() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const active = products.filter((p) => p.status === "active");
  const future = products.filter((p) => p.status !== "active");

  return (
    <div className="pt-36 pb-32">
      <Seo title="Systems — ORAYA" description="Intelligent systems built for complex industries. ConstructONS.AI is ORAYA's first major system." path="/systems" />
      <div className="oraya-container">
        <Reveal>
          <SectionHeader overline="Systems / Products" title="Intelligent systems built for complex industries." subtitle="Each ORAYA system embeds AI into the operating layer of an industry — connecting data, decisions and execution." />
        </Reveal>

        {loading && <p className="text-subtle" data-testid="systems-loading">Loading systems…</p>}
        {!loading && products.length === 0 && <p className="text-subtle" data-testid="systems-empty">No systems published yet.</p>}

        <div className="flex flex-col gap-6">
          {active.map((p) => (
            <Reveal key={p.id || p.slug}>
              <Link to={p.cta_url || `/systems/${p.slug}`} data-testid={`system-card-${p.slug}`}>
                <GlassCard hover className="grid md:grid-cols-2 overflow-hidden p-0">
                  <div className="p-10 md:p-14 flex flex-col justify-between">
                    <div>
                      <Overline>{statusLabel(p.status)}</Overline>
                      <h2 className="display text-3xl md:text-4xl font-medium text-ink mt-5">{p.name}</h2>
                      <p className="text-ink/80 text-lg mt-3">{p.tagline}</p>
                      <p className="text-subtle mt-5 leading-relaxed max-w-md">{p.description}</p>
                    </div>
                    <span className="inline-flex items-center gap-2 text-ink mt-10 font-medium">
                      {p.cta_label || "Explore"} <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </div>
                  <div className="relative min-h-[280px] md:min-h-[400px]">
                    <img src={mediaUrl(p.image) || IMG.constructons} alt={`${p.name} visualization`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-r from-surface/70 to-transparent" />
                  </div>
                </GlassCard>
              </Link>
            </Reveal>
          ))}
        </div>

        {future.length > 0 && (
          <div className="mt-16">
            <Overline className="mb-6 block">Expanding across industries</Overline>
            <div className="grid sm:grid-cols-3 gap-4 md:gap-6">
              {future.map((p, i) => (
                <Reveal key={p.id || p.slug} delay={i * 0.08}>
                  <GlassCard variant="medium" className="p-8 h-full" data-testid={`system-future-${p.slug}`}>
                    <Overline className="text-[10px]">{statusLabel(p.status)}</Overline>
                    <h3 className="display text-xl font-medium text-ink mt-6">{p.name}</h3>
                    <p className="text-sm text-subtle mt-2">{p.tagline}</p>
                  </GlassCard>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
